import { useState, useEffect } from "react";
import RoundedInput from "./RoundedInput";
import { useQuery } from "@apollo/client";
import { BRAND_STORES } from "../core-modules/takeback-queries";
import { useBrand } from "./BrandContext";
import Box from "./Box";
import FormError from "./FormError";
import GridItem from "./GridItem";
import Text from "./Text";
import LozengeButton from "./LozengeButton";
import PropTypes from "prop-types";

function CrossHairsIcon() {
  return (
    <Box mr={1} display="flex" alignItems="center" justifyContent="center">
      <svg
        width="30"
        height="31"
        viewBox="0 0 30 31"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M24.4349 15.2548C24.1923 10.1914 20.205 6.11749 15.2491 5.86933V0.901428H14.2914V5.86933C9.33584 6.11718 5.34855 10.1911 5.10567 15.2548H0.323975V16.2333H5.10567C5.34825 21.2966 9.33551 25.3706 14.2914 25.6187V30.5866H15.2491V25.6187C20.2047 25.3709 24.192 21.2969 24.4349 16.2333H29.218V15.2548H24.4349ZM14.2914 24.6383C9.86392 24.3923 6.30627 20.7571 6.06523 16.2333H9.06062C9.29242 19.0735 11.5115 21.3408 14.2913 21.5777L14.2914 24.6383ZM14.2914 20.5992C12.0399 20.3697 10.243 18.5338 10.0184 16.2333H14.2914V20.5992ZM14.2914 15.2549H10.0184C10.243 12.9544 12.0399 11.1185 14.2914 10.889V15.2549ZM14.2914 9.91048C11.5116 10.1473 9.29259 12.4147 9.06075 15.2549H6.06537C6.30616 10.7329 9.86396 7.09612 14.2916 6.84984L14.2914 9.91048ZM15.2491 10.889C17.5006 11.1185 19.2975 12.9544 19.5221 15.2549H15.2491V10.889ZM15.2491 16.2334H19.5221C19.2975 18.5338 17.5007 20.3698 15.2491 20.5993V16.2334ZM15.2491 24.6384V21.5779C18.0289 21.3411 20.248 19.0737 20.4798 16.2335H23.4752C23.2344 20.7573 19.6767 24.3921 15.2491 24.6384ZM20.4798 15.2549C20.248 12.4146 18.0289 10.1474 15.2491 9.91048V6.84998C19.6748 7.096 23.2343 10.7312 23.4753 15.255L20.4798 15.2549Z"
          fill="black"
        />
      </svg>
    </Box>
  );
}

export default function StoreFinder({
  label,
  name,
  handleChange,
  placeholder,
  error,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredStores, setFilteredStores] = useState([]);
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const { slug } = useBrand();

  const { error: queryError, data } = useQuery(BRAND_STORES, {
    variables: {
      brandSlug: slug,
    },
    skip: !slug,
  });

  const combinedError = queryError || error || locationError;

  useEffect(() => {
    if (data && data.brandStores) {
      const filteredByName = data.brandStores.filter((store) =>
        store.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      const filteredByCity = data.brandStores
        .filter((store) => {
          return store.city.toLowerCase().includes(searchTerm.toLowerCase());
        })
        .filter((store) => {
          return !filteredByName.includes(store);
        })
        .map((store) => ({
          ...store,
          name: `${store.name} (${store.city})`,
        }));
      const filteredByBoth = [...filteredByName, ...filteredByCity];
      setFilteredStores(filteredByBoth);
    }
  }, [data, searchTerm]);

  function handleInputChange(e) {
    setIsOpen(true);
    setSearchTerm(e.target.value);
    setLocationError(null);
    handleChange(null);
  }

  function handleStoreSelect(store) {
    setSearchTerm(store.name);
    handleChange(store.id);
  }

  // Calculate distance between two points using the Haversine formula
  function calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Radius of the earth in km
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(deg2rad(lat1)) *
        Math.cos(deg2rad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c; // Distance in km
    return distance;
  }

  function deg2rad(deg) {
    return deg * (Math.PI / 180);
  }

  function findNearestStore(userLat, userLng) {
    if (!data || !data.brandStores || data.brandStores.length === 0) {
      return null;
    }

    let nearestStore = null;
    let shortestDistance = Infinity;

    data.brandStores.forEach((store) => {
      if (store.latitude && store.longitude) {
        const distance = calculateDistance(
          userLat,
          userLng,
          parseFloat(store.latitude),
          parseFloat(store.longitude)
        );

        if (distance < shortestDistance) {
          shortestDistance = distance;
          nearestStore = store;
        }
      }
    });

    return nearestStore;
  }

  function handleGeolocation() {
    setIsOpen(false);
    setIsLocating(true);
    setLocationError(null);

    if (!navigator.geolocation) {
      setLocationError("Sorry, geolocation not supported by browser");
      setIsLocating(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      function (position) {
        const { latitude, longitude } = position.coords;
        const nearestStore = findNearestStore(latitude, longitude);

        if (nearestStore) {
          handleStoreSelect(nearestStore);
        } else {
          setLocationError("Could not find a nearby store");
        }
        setIsLocating(false);
      },
      function (error) {
        let errorMessage = "Unable to retrieve your location";
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = "Location permission denied";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location information unavailable";
            break;
          case error.TIMEOUT:
            errorMessage = "Location request timed out";
            break;
          default:
            errorMessage = "An unknown error occurred";
        }
        setLocationError(errorMessage);
        setIsLocating(false);
      }
    );
  }

  return (
    <Box position="relative">
      {combinedError && typeof combinedError === "string" && (
        <FormError
          name={name}
          positionProps={{ top: "-20px", left: [0, null, null, 52] }}
        >
          {combinedError}
        </FormError>
      )}
      <div className="store-finder">
        <Box
          display="flex"
          alignItems="center"
          style={{ gap: "10px" }}
          flexWrap="wrap"
        >
          <Box flex="1">
            <RoundedInput
              type="text"
              onChange={handleInputChange}
              onBlur={() => {
                setTimeout(() => {
                  setIsOpen(false);
                }, 200);
              }}
              placeholder={placeholder}
              value={searchTerm}
              autoComplete="off"
              name={name}
              error={!!combinedError}
            />
          </Box>
          <Box width={["100%", null, null, "180px"]}>
            <LozengeButton
              width="100%"
              small
              onClick={handleGeolocation}
              disabled={isLocating}
              invertOnMobile={false}
              noPadding={true}
            >
              <CrossHairsIcon />
              {isLocating ? "Finding..." : "Find store"}
            </LozengeButton>
          </Box>
        </Box>

        {isOpen && searchTerm.length > 0 && (
          <Box
            p={2}
            maxHeight="200px"
            overflow="auto"
            position="absolute"
            backgroundColor="white"
            border="1px solid #ccc"
            top="calc(100% - 20px)"
            zIndex="1000"
            borderRadius="5px"
            width="100%"
          >
            {filteredStores.length > 0 ? (
              <Box as="ul">
                {filteredStores.map((store, index) => (
                  <Box
                    key={`${store.id}-${index}`}
                    as="button"
                    p={1}
                    onClick={() => {
                      handleStoreSelect(store);
                    }}
                    display="block"
                  >
                    {store.name}
                  </Box>
                ))}
              </Box>
            ) : (
              <p>No stores found</p>
            )}
          </Box>
        )}
      </div>
      <GridItem gridColumnStart={2} px={[null, null, null, 52]} pt={20}>
        <Text>{label}</Text>
      </GridItem>
    </Box>
  );
}

StoreFinder.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  handleChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
};
